import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Search, Edit, Trash2, Loader2, UserCircle } from "lucide-react";
import { Button } from "../../../foundation/altzero_base/base/components/ui/button";
import { usePermissions } from "../../../foundation/altzero_base/base/contextapi/PermissionsContext";
import { canCreateContacts, canEditContacts, canDeleteContacts } from "../lib/contactPermissions";
import { Input } from "../../../foundation/altzero_base/base/components/ui/input";
import { useToast } from "../../../foundation/altzero_base/base/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../foundation/altzero_base/base/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../foundation/altzero_base/base/components/ui/table";
import { ResponsiveTable } from "../components/ResponsiveTable";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../foundation/altzero_base/base/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../foundation/altzero_base/base/components/ui/select";
import { Label } from "../../../foundation/altzero_base/base/components/ui/label";
import { Checkbox } from "../../../foundation/altzero_base/base/components/ui/checkbox";
import { Badge } from "../../../foundation/altzero_base/base/components/ui/badge";
import ScopingAILayout from "../components/ScopingAILayout";
import { getContacts, createContact, updateContact, deleteContact, Contact } from "../services/contactService";
import { getClients, Client } from "../services/clientService";

export default function ContactsManagement() {
  /**
   * Contacts used to be reachable by every role, delete included. Each action now asks for its
   * own key — see lib/contactPermissions.ts — so a role such as BROKER can keep a client's
   * contacts current without being able to remove one.
   */
  const { can } = usePermissions();
  const mayCreate = canCreateContacts(can);
  const mayEdit = canEditContacts(can);
  const mayDelete = canDeleteContacts(can);

  const { toast } = useToast();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [clientFilter, setClientFilter] = useState<string>("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [deletingContactId, setDeletingContactId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    client_id: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    mobile: "",
    role: "",
    contact_type: "",
    is_primary: false,
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterContacts();
  }, [searchQuery, clientFilter, contacts]);

  const loadData = async () => {
    setLoading(true);

    // Load clients
    const clientsResult = await getClients();
    if (clientsResult.success && clientsResult.data) {
      setClients(clientsResult.data);
    } else if (clientsResult.error) {
      console.error("Error loading clients:", clientsResult.error);
      toast({
        title: "Error",
        description: clientsResult.error || "Failed to load clients",
        variant: "destructive",
      });
    }

    // Load contacts
    const contactsResult = await getContacts();
    if (contactsResult.success && contactsResult.data) {
      setContacts(contactsResult.data);
    } else if (contactsResult.error) {
      // Only show error toast if there's an actual error (not just empty results)
      toast({
        title: "Error",
        description: contactsResult.error,
        variant: "destructive",
      });
    }

    setLoading(false);
  };

  const filterContacts = () => {
    let filtered = contacts;

    // Filter by client
    if (clientFilter !== "all") {
      filtered = filtered.filter((contact) => contact.client_id === clientFilter);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (contact) =>
          contact.first_name?.toLowerCase().includes(query) ||
          contact.last_name?.toLowerCase().includes(query) ||
          contact.email?.toLowerCase().includes(query) ||
          contact.phone?.toLowerCase().includes(query) ||
          contact.role?.toLowerCase().includes(query)
      );
    }

    setFilteredContacts(filtered);
  };

  const getClientName = (clientId: string) => {
    const client = clients.find((c) => c.id === clientId);
    return client?.name || "Unknown Client";
  };

  const handleOpenDialog = (contact?: Contact) => {
    if (contact) {
      setEditingContact(contact);
      setFormData({
        client_id: contact.client_id,
        first_name: contact.first_name || "",
        last_name: contact.last_name || "",
        email: contact.email || "",
        phone: contact.phone || "",
        mobile: contact.mobile || "",
        role: contact.role || "",
        contact_type: contact.contact_type || "",
        is_primary: contact.is_primary ?? false,
      });
    } else {
      setEditingContact(null);
      setFormData({
        client_id: "",
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        mobile: "",
        role: "",
        contact_type: "",
        is_primary: false,
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingContact(null);
    setFormData({
      client_id: "",
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      mobile: "",
      role: "",
      contact_type: "",
      is_primary: false,
    });
  };

  const handleSubmit = async () => {
    if (!formData.client_id) {
      toast({
        title: "Validation Error",
        description: "Please select a client",
        variant: "destructive",
      });
      return;
    }

    const result = editingContact
      ? await updateContact(editingContact.id, {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone,
          mobile: formData.mobile,
          role: formData.role,
          contact_type: formData.contact_type,
          is_primary: formData.is_primary,
        })
      : await createContact(formData);

    if (result.success) {
      toast({
        title: "Success",
        description: `Contact ${editingContact ? "updated" : "created"} successfully`,
      });
      handleCloseDialog();
      loadData();
    } else {
      toast({
        title: "Error",
        description: result.error || `Failed to ${editingContact ? "update" : "create"} contact`,
        variant: "destructive",
      });
    }
  };

  const handleDeleteClick = (contactId: string) => {
    setDeletingContactId(contactId);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingContactId) return;

    const result = await deleteContact(deletingContactId);
    if (result.success) {
      toast({
        title: "Success",
        description: "Contact deleted successfully",
      });
      loadData();
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to delete contact",
        variant: "destructive",
      });
    }
    setIsDeleteDialogOpen(false);
    setDeletingContactId(null);
  };

  return (
    <ScopingAILayout>
      <div className="container mx-auto px-6 py-8">
        <div className="space-y-6">
          {/* Header */}
          <div>
            <Link
              to="/tender/resource-library"
              className="flex items-center text-muted-foreground hover:text-foreground mb-4"
            >
              <ArrowLeft size={16} className="mr-2" />
              Back to Resource Library
            </Link>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-primary/10 p-3 rounded-lg">
                  <UserCircle className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold">Contacts</h1>
                  <p className="text-muted-foreground">
                    Manage client contact persons
                  </p>
                </div>
              </div>
              {mayCreate && (
                <Button onClick={() => handleOpenDialog()} disabled={clients.length === 0}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Contact
                </Button>
              )}
            </div>
          </div>

          {clients.length === 0 ? (
            <div className="border rounded-lg p-12 text-center">
              <UserCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-2">No clients found</p>
              <p className="text-sm text-muted-foreground mb-4">
                You need to create a client first before adding contacts
              </p>
              <Link to="/tender/resource-library/clients">
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Client
                </Button>
              </Link>
            </div>
          ) : (
            <>
              {/* Filters */}
              <div className="flex items-center gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder="Search contacts by name, email, phone, or role..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={clientFilter} onValueChange={setClientFilter}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Filter by client" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Clients</SelectItem>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Table */}
              <div className="border rounded-lg">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : filteredContacts.length === 0 ? (
                  <div className="text-center py-12">
                    <UserCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-2">
                      {searchQuery || clientFilter !== "all" ? "No contacts found" : "No contacts yet"}
                    </p>
                    <p className="text-sm text-muted-foreground mb-4">
                      {searchQuery || clientFilter !== "all"
                        ? "Try adjusting your filters"
                        : "Get started by adding your first contact"}
                    </p>
                    {!searchQuery && clientFilter === "all" && mayCreate && (
                      <Button onClick={() => handleOpenDialog()}>
                        <Plus className="mr-2 h-4 w-4" />
                        Add Contact
                      </Button>
                    )}
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Mobile</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Contact Type</TableHead>
                        <TableHead>Primary</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredContacts.map((contact) => (
                        <TableRow key={contact.id}>
                          <TableCell className="font-medium">
                            {`${contact.first_name || ""} ${contact.last_name || ""}`.trim() || "-"}
                          </TableCell>
                          <TableCell>{contact.email || "-"}</TableCell>
                          <TableCell>{contact.phone || "-"}</TableCell>
                          <TableCell>{contact.mobile || "-"}</TableCell>
                          <TableCell>{contact.role || "-"}</TableCell>
                          <TableCell>{contact.contact_type || "-"}</TableCell>
                          <TableCell>
                            {contact.is_primary ? <Badge variant="default">Primary</Badge> : "-"}
                          </TableCell>
                          <TableCell>{getClientName(contact.client_id)}</TableCell>
                          <TableCell>
                            {new Date(contact.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              {mayEdit && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleOpenDialog(contact)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              )}
                              {mayDelete && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteClick(contact.id)}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingContact ? "Edit Contact" : "Add New Contact"}
            </DialogTitle>
            <DialogDescription>
              {editingContact
                ? "Update contact information"
                : "Add a new contact person"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="client_id">Client *</Label>
              <Select
                value={formData.client_id}
                onValueChange={(value) =>
                  setFormData({ ...formData, client_id: value })
                }
                disabled={!!editingContact}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) =>
                    setFormData({ ...formData, first_name: e.target.value })
                  }
                  placeholder="Enter first name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) =>
                    setFormData({ ...formData, last_name: e.target.value })
                  }
                  placeholder="Enter last name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder="Enter email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                placeholder="Enter phone number"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mobile">Mobile</Label>
              <Input
                id="mobile"
                value={formData.mobile}
                onChange={(e) =>
                  setFormData({ ...formData, mobile: e.target.value })
                }
                placeholder="Enter mobile number"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Input
                  id="role"
                  value={formData.role}
                  onChange={(e) =>
                    setFormData({ ...formData, role: e.target.value })
                  }
                  placeholder="e.g., Energy Manager, CFO"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_type">Contact Type</Label>
                <Input
                  id="contact_type"
                  value={formData.contact_type}
                  onChange={(e) =>
                    setFormData({ ...formData, contact_type: e.target.value })
                  }
                  placeholder="e.g., Billing, Site"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="is_primary"
                checked={formData.is_primary}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, is_primary: checked === true })
                }
              />
              <Label htmlFor="is_primary">Primary contact</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              {editingContact ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this contact. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ScopingAILayout>
  );
}

